import { createPortal } from 'react-dom';
import { User, X } from 'lucide-react';
import { ContactDTO } from '@/types';
import { useModalLayer, LayerDepthContext } from '@/lib/layerManager';
import { useIsMobile } from '@/hooks/useIsMobile';
import { ContactDetailsPanel } from '../ContactsPage';

interface JobContactInfoModalProps {
  jobId: string;
  /** The job's contacts — one tab each when there are several. */
  contacts: ContactDTO[];
  /** The contact whose tab is showing. */
  contactId: string;
  onSelectContact: (contactId: string) => void;
  onClose: () => void;
  /** Estimates/leads open as page-level overlays, so the job has to get out of the way first. */
  onBeforeNavigate: () => void;
}

/** The Contacts page's details panel, opened from a job (desktop footer / contact rows, mobile contacts sheet). */
export function JobContactInfoModal({ jobId, contacts, contactId, onSelectContact, onClose, onBeforeNavigate }: JobContactInfoModalProps) {
  const isMobile = useIsMobile();
  const layer = useModalLayer({ enabled: true, onClose });

  // Portaled so it shares the body stacking context with the (portaled) mobile contacts sheet; no
  // backdrop-blur on the containers — a past job opened from the panel is position:fixed inside them.
  return createPortal(
    <div
      className={isMobile ? 'fixed inset-0 flex flex-col bg-white safe-area-top safe-area-bottom' : 'fixed inset-0 flex items-center justify-center p-4'}
      style={layer.style}
    >
      {!isMobile && <div className="absolute inset-0 bg-black/50" onClick={layer.onClose} />}
      <div className={isMobile ? 'flex flex-1 flex-col min-h-0' : 'relative bg-white rounded-xl shadow-2xl flex flex-col w-full max-w-2xl h-[85vh] overflow-hidden'}>
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <User className="h-5 w-5" /> Contact Info
          </h2>
          <button onClick={layer.onClose} className="p-2 -mr-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>
        {contacts.length > 1 && (
          <div className="flex gap-1 px-4 border-b border-gray-200 overflow-x-auto shrink-0">
            {contacts.map((contact) => (
              <button
                key={contact.id}
                onClick={() => onSelectContact(contact.id)}
                className={`px-3 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${contact.id === contactId ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              >
                {contact.name || 'Contact'}
              </button>
            ))}
          </div>
        )}
        <div className="flex-1 min-h-0 overflow-auto">
          <LayerDepthContext.Provider value={layer.depth}>
            <ContactDetailsPanel contactId={contactId} jobId={jobId} onBeforeNavigate={onBeforeNavigate} />
          </LayerDepthContext.Provider>
        </div>
      </div>
    </div>,
    document.body
  );
}
